import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useId, useRef, useState, } from 'react';
import { cn } from './cn.js';
const TabsContext = createContext(null);
function useTabs(component) {
    const ctx = useContext(TabsContext);
    if (!ctx)
        throw new Error(`<${component}> must be used within <Tabs>`);
    return ctx;
}
const tabId = (base, value) => `${base}-tab-${value}`;
const panelId = (base, value) => `${base}-panel-${value}`;
/** Accessible tabs root (WAI-ARIA tabs pattern). Controlled or uncontrolled. */
export function Tabs({ value, defaultValue, onValueChange, className, children, ...props }) {
    const baseId = useId();
    const [internal, setInternal] = useState(defaultValue ?? '');
    const active = value ?? internal;
    const setValue = (next) => {
        if (value === undefined)
            setInternal(next);
        onValueChange?.(next);
    };
    return (_jsx(TabsContext.Provider, { value: { value: active, setValue, baseId }, children: _jsx("div", { className: cn('flex flex-col gap-4', className), ...props, children: children }) }));
}
/** Tab strip. Owns arrow/Home/End keyboard navigation with automatic activation. */
export function TabsList({ className, children, ...props }) {
    const { setValue } = useTabs('TabsList');
    const ref = useRef(null);
    const onKeyDown = (e) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(e.key) || !ref.current)
            return;
        const tabs = Array.from(ref.current.querySelectorAll('[role="tab"]:not([disabled])'));
        if (tabs.length === 0)
            return;
        const current = tabs.indexOf(document.activeElement);
        let next = current;
        if (e.key === 'ArrowRight')
            next = (current + 1) % tabs.length;
        else if (e.key === 'ArrowLeft')
            next = (current - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home')
            next = 0;
        else if (e.key === 'End')
            next = tabs.length - 1;
        e.preventDefault();
        const target = tabs[next];
        target.focus();
        const value = target.dataset.value;
        if (value)
            setValue(value);
    };
    return (_jsx("div", { ref: ref, role: "tablist", "aria-orientation": "horizontal", onKeyDown: onKeyDown, className: cn('inline-flex items-center gap-1 border-b border-border', className), ...props, children: children }));
}
/** A single tab. Selected tab is the only tab-stop (roving tabindex). */
export function TabsTrigger({ value, className, children, ...props }) {
    const { value: active, setValue, baseId } = useTabs('TabsTrigger');
    const selected = active === value;
    return (_jsxs("button", { type: "button", role: "tab", id: tabId(baseId, value), "data-value": value, "aria-selected": selected, "aria-controls": panelId(baseId, value), tabIndex: selected ? 0 : -1, onClick: () => setValue(value), className: cn('relative -mb-px inline-flex h-10 items-center whitespace-nowrap px-4 text-sm font-medium ' +
            'transition-colors duration-[var(--dur-quick)] ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ' +
            'disabled:pointer-events-none disabled:text-disabled-foreground', selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground', className), ...props, children: [children, _jsx("span", { "aria-hidden": "true", className: cn('absolute inset-x-0 -bottom-px h-0.5 origin-left rounded-full bg-primary ' +
                    'transition-transform duration-[var(--dur-base)] ease-[var(--ease-out)] motion-reduce:transition-none', selected ? 'scale-x-100' : 'scale-x-0') })] }));
}
/** Panel for a tab. Hidden (and removed from tab order) unless its tab is active. */
export function TabsContent({ value, className, children, ...props }) {
    const { value: active, baseId } = useTabs('TabsContent');
    const selected = active === value;
    return (_jsx("div", { role: "tabpanel", id: panelId(baseId, value), "aria-labelledby": tabId(baseId, value), hidden: !selected, tabIndex: 0, className: cn('focus-visible:outline-none', className), ...props, children: selected ? children : null }));
}
